
import React from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import { CampaignMetadata, LeadCardData } from '../types';
import { InviteManagementModal } from './InviteManagementModal';
import { useAuth } from '../contexts/AuthContext';
import { canManageInvites } from '../utils/invites';
import {
  getCampaignEnrichedLeadCount,
  getCampaignVisibleLeadCount,
  getEnrichedLeads,
  getVisibleLeads,
  isEnrichedLead,
} from '../utils/campaignMetrics';
import { getNormalizedStatusKey, getStatusConfig } from '../utils/statusMapping';
import { formatCampaignStatusLabel } from '../utils/campaignLifecycleStatus';

type CampaignDetailsTab = 'brief' | 'leads' | 'audience' | 'execution' | 'offer' | 'notes' | 'admin';

interface CampaignDetailsModalProps {
  campaign: CampaignMetadata | null;
  onClose: () => void;
  onDelete?: (campaignId: string) => void;
  leads?: LeadCardData[];
  leadsLoading?: boolean;
  onOpenLead?: (lead: LeadCardData) => void;
  onOpenLeadTable?: (campaignId: string) => void;
  /** URL-driven active tab (?tab=). */
  activeTab?: CampaignDetailsTab;
  onTabChange?: (tab: CampaignDetailsTab) => void;
}

export const CampaignDetailsModal: React.FC<CampaignDetailsModalProps> = ({ campaign, onClose, onDelete, leads = [], leadsLoading = false, onOpenLead, onOpenLeadTable, activeTab: activeTabProp, onTabChange }) => {
  const { currentUser } = useAuth();
  const [showInviteModal, setShowInviteModal] = React.useState(false);
  const [localTab, setLocalTab] = React.useState<CampaignDetailsTab>('brief');
  const activeTab = activeTabProp ?? localTab;
  const setActiveTab = (tab: CampaignDetailsTab) => {
    setLocalTab(tab);
    onTabChange?.(tab);
  };
  const canRender = Boolean(campaign) && typeof document !== 'undefined';

  React.useEffect(() => {
    // Reset to Brief on campaign change unless the URL pins a tab.
    if (!activeTabProp) setLocalTab('brief');
  }, [campaign?.id, activeTabProp]);

  React.useEffect(() => {
    if (!canRender) return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [canRender]);

  if (!campaign || typeof document === 'undefined') return null;

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatCompactCount = (count: number): string => {
    if (count >= 1000) return `${(count / 1000).toFixed(count >= 10000 ? 0 : 1)}k`;
    return count.toLocaleString();
  };

  const getInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join('');
  };

  const rawCampaign = campaign as CampaignMetadata & Record<string, any>;

  const isTruthyFlag = (value: unknown): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes';
    }
    return false;
  };

  const extractMeaningfulText = (value: any): string[] => {
    if (value === null || value === undefined) return [];
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed ? [trimmed] : [];
    }
    if (Array.isArray(value)) {
      return value.flatMap((item) => extractMeaningfulText(item));
    }
    if (typeof value === 'object') {
      return Object.values(value).flatMap((item) => extractMeaningfulText(item));
    }
    return [];
  };

  const getFirstMeaningfulValue = (...values: any[]): string | null => {
    for (const value of values) {
      const extracted = extractMeaningfulText(value);
      if (extracted.length > 0) {
        return extracted[0];
      }
    }
    return null;
  };

  const getCustomFieldValue = (fieldName: string): string | null => {
    const customFieldContainers = [
      rawCampaign.customfields,
      rawCampaign.customFields,
      rawCampaign.custom_fields,
      rawCampaign.custom_fields_data,
    ];

    for (const container of customFieldContainers) {
      if (!container) continue;

      if (Array.isArray(container)) {
        for (const field of container) {
          if (!field || typeof field !== 'object') continue;

          const name = getFirstMeaningfulValue(field.name, field.key, field.label, field.fieldName);
          if (name?.trim().toLowerCase() !== fieldName.trim().toLowerCase()) continue;

          const value = getFirstMeaningfulValue(
            field.value,
            field.text,
            field.content,
            field.answer,
            field.fieldValue
          );
          if (value) return value;
        }
      }

      if (typeof container === 'object') {
        const directValue = getFirstMeaningfulValue(
          container[fieldName],
          container[fieldName.toLowerCase()],
          container[fieldName.replace(/\s+/g, '_')],
          container[fieldName.toLowerCase().replace(/\s+/g, '_')]
        );
        if (directValue) return directValue;
      }
    }

    return null;
  };

  const campaignSummaryValue = extractMeaningfulText(campaign.summary).length > 0 ? campaign.summary : null;
  const campaignDesiresValue = getFirstMeaningfulValue(
    campaign.desires,
    rawCampaign.campaign_desires,
    rawCampaign.desires_and_goals,
    getCustomFieldValue('Campaign Desires')
  );
  const uspValue = getFirstMeaningfulValue(
    campaign.usp,
    campaign.unique_sales_point,
    campaign.unique_selling_point,
    rawCampaign.usp,
    rawCampaign.unique_sales_point,
    rawCampaign.unique_selling_point,
    rawCampaign.unique_value_proposition
  );
  const guaranteeValue = getFirstMeaningfulValue(
    campaign.guarantee,
    rawCampaign.guarantee,
    rawCampaign.offer_guarantee
  );
  const offerPricingValue = getFirstMeaningfulValue(
    campaign.offer_pricing,
    rawCampaign.offer_pricing,
    rawCampaign.pricing,
    rawCampaign.offer_price
  );
  const availableOutreachAssetsValue = extractMeaningfulText(
    rawCampaign.available_outreach_assets ??
    rawCampaign.outreach_assets ??
    rawCampaign.assets_available ??
    rawCampaign.available_assets
  );
  const outreachChannelLabels = Array.isArray(campaign.outreach_channel)
    ? campaign.outreach_channel
        .map((channel) => {
          if (typeof channel === 'string') return channel.trim();
          if (channel && typeof channel === 'object' && typeof channel.value === 'string') return channel.value.trim();
          return '';
        })
        .filter((channel) => channel.length > 0)
    : [];
  const leadGeneratorSourceRaw = campaign.source || getFirstMeaningfulValue(
    rawCampaign.generator_id,
    rawCampaign.source,
    rawCampaign.lead_generator_source,
    rawCampaign.generator_source,
    rawCampaign.lead_source
  );
  const leadGeneratorSourceLabel = leadGeneratorSourceRaw
    ? leadGeneratorSourceRaw.trim().toUpperCase() === 'MANUAL MODE'
      ? 'FILE'
      : 'BRAINIACS ENGINE'
    : null;
  const outreachBlocked = isTruthyFlag(rawCampaign.block_outreach);
  const meetingUrlValue = getFirstMeaningfulValue(campaign.meeting_url, rawCampaign.campaign_meeting_url);
  const campaignLeads = leads.filter((lead) => !(lead as any).isPlaceholder);
  const visibleLeads = getVisibleLeads(campaignLeads);
  const enrichedLeads = getEnrichedLeads(campaignLeads);
  const totalLeadCount = campaignLeads.length > 0 ? visibleLeads.length : getCampaignVisibleLeadCount(campaign);
  const enrichedLeadCount = campaignLeads.length > 0 ? enrichedLeads.length : getCampaignEnrichedLeadCount(campaign);
  const pendingEnrichmentCount = Math.max(totalLeadCount - enrichedLeadCount, 0);
  const enrichmentRate = totalLeadCount > 0 ? Math.round((enrichedLeadCount / totalLeadCount) * 100) : 0;
  const leadRows = campaignLeads.slice(0, 80);

  const objectiveBadge = campaign.primary_campaign_objective || getFirstMeaningfulValue(rawCampaign.campaign_goal, rawCampaign.goal);
  // SHARED label formatter — the old local version title-cased the RAW field
  // ("archive" → "Archive"), so this modal could print a different status word
  // than the pill on the card that opened it. Routes through the same lifecycle
  // rules as the card's colour and the select's value.
  const formatStatusLabel = (value: string | null | undefined) => {
    if (!value) return 'Not set';
    return formatCampaignStatusLabel(value);
  };

  const headerMetrics = [
    { label: 'Total Leads', value: formatCompactCount(totalLeadCount) },
    { label: 'Enriched', value: `${formatCompactCount(enrichedLeadCount)} (${enrichmentRate}%)` },
    { label: 'Weekly Limit', value: campaign.weekly_limit ? `${campaign.weekly_limit} leads / week` : 'Not configured' },
    { label: 'Outreach', value: outreachBlocked ? 'Blocked' : 'Open' },
    { label: 'Lead Source', value: leadGeneratorSourceLabel || 'Unknown' },
  ];

  const systemMetrics = [
    { label: 'Location', value: campaign.search_location || 'Not set' },
    { label: 'Preferred Language', value: campaign.preferred_language || 'Not set' },
    { label: 'Meeting URL', value: meetingUrlValue || 'Not configured' },
    { label: 'Campaign ID', value: campaign.id },
  ];

  const attentionItems = [
    !campaign.search_location ? 'Add a search location so lead generation can be geographically targeted.' : null,
    !campaign.target_audience_niche ? 'Define the target niche to sharpen audience selection and messaging.' : null,
    !meetingUrlValue ? 'Add a meeting URL so outreach has a clear conversion destination.' : null,
    outreachChannelLabels.length === 0 ? 'Choose outreach channels so the team knows where this campaign should run.' : null,
    !campaign.core_value_proposition ? 'Document the core value proposition so offer messaging stays consistent.' : null,
  ].filter(Boolean) as string[];

  const tabItems: Array<{ key: CampaignDetailsTab; label: string }> = [
    { key: 'brief', label: 'Brief' },
    { key: 'leads', label: 'Leads' },
    { key: 'audience', label: 'Audience' },
    { key: 'execution', label: 'Execution' },
    { key: 'offer', label: 'Offer' },
    { key: 'notes', label: 'Notes' },
    { key: 'admin', label: 'Admin' },
  ];

  const renderCalloutList = (items: string[], emptyMessage: string) => {
    if (items.length === 0) {
      return <div className="text-sm text-gray-400">{emptyMessage}</div>;
    }

    return (
      <div className="space-y-2.5">
        {items.map((item) => (
          <div key={item} className="rounded-xl bg-black/20 px-4 py-3 text-sm leading-6 text-gray-200">
            {item}
          </div>
        ))}
      </div>
    );
  };

  const renderSectionCard = (
    title: string,
    children: React.ReactNode,
    options?: { subtitle?: string; accent?: 'blue' | 'cyan' | 'emerald' | 'amber' | 'rose' | 'slate' }
  ) => {
    const accentClassMap = {
      blue: 'border-[#5260FF]/20 bg-[linear-gradient(145deg,rgba(82,96,255,0.08),rgba(255,255,255,0.02))] text-[#C7D2FF]',
      cyan: 'border-cyan-400/20 bg-[linear-gradient(145deg,rgba(34,211,238,0.08),rgba(255,255,255,0.02))] text-cyan-100',
      emerald: 'border-emerald-400/20 bg-[linear-gradient(145deg,rgba(16,185,129,0.08),rgba(255,255,255,0.02))] text-emerald-100',
      amber: 'border-amber-400/20 bg-[linear-gradient(145deg,rgba(251,191,36,0.08),rgba(255,255,255,0.02))] text-amber-100',
      rose: 'border-rose-400/20 bg-[linear-gradient(145deg,rgba(244,63,94,0.08),rgba(255,255,255,0.02))] text-rose-100',
      slate: 'border-white/10 bg-[linear-gradient(145deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] text-white',
    } as const;
    const accent = options?.accent || 'slate';

    return (
      <section className={`rounded-[22px] border p-4 lg:p-5 ${accentClassMap[accent]}`}>
        <div className="mb-3">
          <h3 className="text-[11px] font-black uppercase tracking-[0.22em] text-current">{title}</h3>
          {options?.subtitle && <p className="mt-2 text-sm leading-6 text-gray-400">{options.subtitle}</p>}
        </div>
        <div className="text-sm text-white">{children}</div>
      </section>
    );
  };

  const renderMetricGrid = (items: Array<{ label: string; value: string }>, columns: 'compact' | 'spacious' = 'compact') => (
    <div className={`grid grid-cols-1 gap-3 ${columns === 'spacious' ? 'md:grid-cols-2 xl:grid-cols-4' : 'sm:grid-cols-2'}`}>
      {items.map((item) => (
        <div key={item.label} className="rounded-xl bg-black/25 p-3.5">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-gray-500">{item.label}</div>
          <div className="mt-2 text-sm font-semibold text-white">{item.value}</div>
        </div>
      ))}
    </div>
  );

  const renderChipList = (items: string[], tone: 'blue' | 'cyan' | 'emerald' | 'slate' = 'slate') => {
    const toneClassMap = {
      blue: 'border-[#5260FF]/30 bg-[#5260FF]/10 text-[#C7D2FF]',
      cyan: 'border-cyan-400/25 bg-cyan-400/10 text-cyan-100',
      emerald: 'border-emerald-400/25 bg-emerald-400/10 text-emerald-100',
      slate: 'border-white/10 bg-white/[0.05] text-gray-200',
    } as const;

    if (items.length === 0) {
      return <div className="text-sm text-gray-400">No data configured.</div>;
    }

    return (
      <div className="flex flex-wrap gap-2">
        {items.map((item) => (
          <span key={item} className={`rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.08em] ${toneClassMap[tone]}`}>
            {item}
          </span>
        ))}
      </div>
    );
  };

  const renderRichTextBlock = (value: any) => {
    if (!value) return <div className="text-sm text-gray-400">No data configured.</div>;

    const textValues = Array.isArray(value) ? extractMeaningfulText(value) : [String(value)];

    return (
      <div className="space-y-3">
        {textValues.map((text, index) => (
          <div key={`${index}-${text.slice(0, 24)}`} className="rounded-xl bg-black/20 p-4">
            <p className="whitespace-pre-wrap text-sm leading-7 text-gray-200">{text}</p>
          </div>
        ))}
      </div>
    );
  };

  const renderLeadStatusChip = (lead: LeadCardData) => {
    const normalizedStatus = getNormalizedStatusKey(lead.status);
    const status = getStatusConfig(normalizedStatus);

    return (
      <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${status.tailwindBorder} ${status.tailwindBg} ${status.tailwindColor}`}>
        <span className={`h-1.5 w-1.5 rounded-full ${status.dotColor}`} />
        {status.label}
      </span>
    );
  };

  const renderLeadsPanel = () => (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-[18px] border border-white/10 bg-black/20 p-4">
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40">Total Leads</div>
          <div className="mt-2 text-3xl font-black text-white">{leadsLoading && campaignLeads.length === 0 ? '...' : formatCompactCount(totalLeadCount)}</div>
        </div>
        <div className="rounded-[18px] border border-blue-400/20 bg-blue-400/10 p-4">
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-100/70">Enriched</div>
          <div className="mt-2 flex items-end gap-2">
            <span className="text-3xl font-black text-blue-100">{leadsLoading && campaignLeads.length === 0 ? '...' : formatCompactCount(enrichedLeadCount)}</span>
            <span className="pb-1 text-xs font-black uppercase tracking-[0.16em] text-blue-100/60">{enrichmentRate}%</span>
          </div>
        </div>
        <div className="rounded-[18px] border border-amber-400/20 bg-amber-400/10 p-4">
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-100/70">Pending Enrichment</div>
          <div className="mt-2 text-3xl font-black text-amber-100">{leadsLoading && campaignLeads.length === 0 ? '...' : formatCompactCount(pendingEnrichmentCount)}</div>
        </div>
      </div>

      <section className="overflow-hidden rounded-[22px] border border-white/10 bg-[linear-gradient(180deg,rgba(12,13,17,0.96),rgba(7,8,10,0.98))]">
        <div className="flex flex-col gap-3 border-b border-white/8 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/40">Leads Intelligence</div>
            <p className="mt-1 text-xs leading-5 text-gray-400">People in this campaign, pulled from the Leads Intelligence data set.</p>
          </div>
          {onOpenLeadTable && (
            <button
              type="button"
              onClick={() => onOpenLeadTable(campaign.id)}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-cyan-300/20 bg-cyan-400/10 px-3 py-2 text-[11px] font-black uppercase tracking-[0.14em] text-cyan-100 transition-colors hover:border-cyan-300/35 hover:bg-cyan-400/15"
            >
              <Icon icon="bx:table" className="h-4 w-4" />
              Full Table
            </button>
          )}
        </div>

        {leadsLoading && campaignLeads.length === 0 ? (
          <div className="flex items-center justify-center gap-3 px-4 py-10 text-sm font-semibold text-gray-300">
            <Icon icon="bx:loader-alt" className="h-5 w-5 animate-spin text-cyan-200" />
            Loading campaign leads...
          </div>
        ) : leadRows.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] text-white/50">
              <Icon icon="bx:user-search" className="h-6 w-6" />
            </div>
            <div className="mt-4 text-sm font-semibold text-white">No leads loaded yet</div>
            <p className="mt-2 text-sm leading-6 text-gray-400">Open the full table or wait for this campaign to finish generating leads.</p>
          </div>
        ) : (
          <div className="max-h-[52vh] overflow-y-auto">
            {leadRows.map((lead) => {
              const enriched = isEnrichedLead(lead);
              const location = [lead.companyCity, lead.companyCountry].filter(Boolean).join(', ');
              const rowContent = (
                <>
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.05] text-xs font-black text-white">
                    {getInitials(lead.leadName || lead.companyName || 'Lead')}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-black text-white">{lead.leadName || 'Unnamed lead'}</span>
                      {enriched && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-blue-300/20 bg-blue-400/10 px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.12em] text-blue-100">
                          <Icon icon="bx:sparkles" className="h-3 w-3" />
                          Enriched
                        </span>
                      )}
                    </div>
                    <div className="mt-1 truncate text-xs text-gray-400">
                      {[lead.leadRole, lead.companyName, location].filter(Boolean).join(' - ')}
                    </div>
                  </div>
                  <div className="hidden shrink-0 items-center gap-2 lg:flex">
                    {renderLeadStatusChip(lead)}
                    {typeof lead.icpScore === 'number' && (
                      <span className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-white/65">
                        ICP {lead.icpScore}
                      </span>
                    )}
                  </div>
                  <Icon icon="bx:chevron-right" className="h-4 w-4 shrink-0 text-white/30" />
                </>
              );

              return onOpenLead ? (
                <button
                  key={`${lead.campaignId}-${lead.id}`}
                  type="button"
                  onClick={() => onOpenLead(lead)}
                  className="flex w-full items-center gap-3 border-b border-white/[0.06] px-4 py-3 text-left transition-colors hover:bg-white/[0.035]"
                >
                  {rowContent}
                </button>
              ) : (
                <div key={`${lead.campaignId}-${lead.id}`} className="flex items-center gap-3 border-b border-white/[0.06] px-4 py-3">
                  {rowContent}
                </div>
              );
            })}
            {campaignLeads.length > leadRows.length && (
              <div className="px-4 py-3 text-center text-xs font-semibold text-gray-400">
                Showing first {leadRows.length.toLocaleString()} of {campaignLeads.length.toLocaleString()} leads. Open the full table for bulk actions and filters.
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[120] overflow-y-auto bg-black/90 p-3 pt-3 backdrop-blur-md lg:p-4 lg:pt-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="flex min-h-full items-start justify-center">
        <div
          className="mx-auto flex max-h-[calc(100vh-1.5rem)] w-full max-w-5xl flex-col overflow-hidden rounded-[28px] border border-white/8 bg-[radial-gradient(circle_at_top_left,rgba(82,96,255,0.08),transparent_28%),linear-gradient(160deg,#111214,#090909)] shadow-[0_30px_100px_rgba(0,0,0,0.55)] lg:max-h-[calc(100vh-2rem)]"
          onClick={(event) => event.stopPropagation()}
        >
        <div className="shrink-0 border-b border-white/10 px-5 py-4 lg:px-6 lg:py-4.5">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-black uppercase tracking-[0.22em] text-gray-400">
                  Campaign Details
                </span>
                <span className="text-[10px] font-black uppercase tracking-[0.18em] text-gray-400">
                  {formatStatusLabel(campaign.status)}
                </span>
                {leadGeneratorSourceLabel && (
                  <span className="rounded-full border border-cyan-400/25 bg-cyan-400/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-cyan-100">
                    {leadGeneratorSourceLabel}
                  </span>
                )}
                <span className={`rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] ${outreachBlocked ? 'border-rose-400/25 bg-rose-400/10 text-rose-100' : 'border-emerald-400/25 bg-emerald-400/10 text-emerald-100'}`}>
                  {outreachBlocked ? 'Outreach Blocked' : 'Outreach Open'}
                </span>
              </div>
              <h2 className="mt-3 max-w-4xl text-2xl font-black tracking-tight text-white lg:text-[2rem]">
                {campaign.name}
              </h2>
              <p className="mt-2 max-w-4xl text-sm leading-6 text-gray-300">
                {objectiveBadge || 'Campaign objective has not been defined yet.'}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.16em] text-gray-400">
                {campaign.client_name && <span>Client {campaign.client_name}</span>}
                <span>Created {formatDate(campaign.created || campaign.createdAt)}</span>
                {campaign.updatedAt && <span>Updated {formatDate(campaign.updatedAt)}</span>}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {outreachChannelLabels.length > 0 && renderChipList(outreachChannelLabels, 'cyan')}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2">
              {canManageInvites(campaign, currentUser?.email) && (
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="inline-flex items-center gap-2 rounded-xl border border-[#5260FF]/25 bg-[#5260FF]/10 px-4 py-2.5 text-sm font-semibold text-[#C7D2FF] transition-all hover:border-[#5260FF]/45 hover:bg-[#5260FF]/15"
                  title="Manage campaign access"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"/>
                  </svg>
                  Invite
                </button>
              )}
              {onDelete && (
                <button
                  onClick={() => onDelete(campaign.id)}
                  className="inline-flex items-center gap-2 rounded-xl border border-amber-400/25 bg-amber-400/10 px-4 py-2.5 text-sm font-semibold text-amber-200 transition-all hover:border-amber-400/45 hover:bg-amber-400/15"
                  title="Archive this campaign"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/>
                  </svg>
                  Archive
                </button>
              )}
              <button
                onClick={onClose}
                className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-gray-400 transition-all hover:border-white/20 hover:text-white"
                aria-label="Close"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="mt-4">
            {renderMetricGrid(headerMetrics, 'spacious')}
          </div>
        </div>

        <div className="shrink-0 border-b border-white/8 px-5 py-3 lg:px-6">
          <div className="flex flex-wrap gap-2">
            {tabItems.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-[0.16em] transition-all ${
                  activeTab === tab.key
                    ? 'border border-white/10 bg-white/[0.08] text-white shadow-[0_10px_30px_rgba(0,0,0,0.25)]'
                    : 'border border-transparent bg-transparent text-gray-500 hover:border-white/6 hover:bg-white/[0.03] hover:text-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 lg:px-6 lg:py-5">
          {activeTab === 'brief' && (
            <div className="space-y-5">
              {renderSectionCard(
                'What Matters',
                <div className="space-y-4">
                  {renderRichTextBlock(campaignDesiresValue || objectiveBadge || 'No campaign brief available yet.')}
                  <div>
                    <div className="mb-2 text-[10px] font-black uppercase tracking-[0.16em] text-gray-500">Outreach Channels</div>
                    {renderChipList(outreachChannelLabels, 'cyan')}
                  </div>
                </div>,
                { subtitle: 'The quickest way to understand the campaign goal, intent, and live channels.', accent: 'cyan' }
              )}

              <div className="grid gap-5 xl:grid-cols-[1.1fr_0.9fr]">
                {renderSectionCard(
                  'Operating Snapshot',
                  <div className="space-y-4">
                    {renderMetricGrid(systemMetrics)}
                    <div>
                      <div className="mb-2 text-[10px] font-black uppercase tracking-[0.16em] text-gray-500">Available Outreach Assets</div>
                      {renderChipList(availableOutreachAssetsValue, 'slate')}
                    </div>
                  </div>,
                  { subtitle: 'Critical setup details the team needs before touching campaign execution.', accent: 'blue' }
                )}

                {renderSectionCard(
                  'Needs Attention',
                  renderCalloutList(attentionItems, 'This campaign has the core information it needs for day-to-day use.'),
                  { subtitle: 'Missing or incomplete setup items that are most likely to slow the team down.', accent: 'amber' }
                )}
              </div>
            </div>
          )}

          {activeTab === 'leads' && renderLeadsPanel()}

          {activeTab === 'audience' && (
            <div className="grid gap-5 xl:grid-cols-2">
              {renderSectionCard(
                'Target Niche',
                renderRichTextBlock(campaign.target_audience_niche || 'No target niche defined.'),
                { subtitle: 'Who this campaign is aimed at and how the audience is framed.', accent: 'blue' }
              )}
              {renderSectionCard(
                'Buyer Personas',
                renderRichTextBlock(campaign.buyer_personas || 'No buyer personas provided.'),
                { subtitle: 'Persona notes and segmentation for the ideal contacts.', accent: 'cyan' }
              )}
              {renderSectionCard(
                'Pain Points',
                renderRichTextBlock(campaign.target_audience_pain_points || 'No pain points documented.'),
                { subtitle: 'Problems and frustrations the outreach should connect to.', accent: 'rose' }
              )}
              {campaignDesiresValue && renderSectionCard(
                'Audience Desires',
                renderRichTextBlock(campaignDesiresValue),
                { subtitle: 'What the audience is ultimately trying to achieve.', accent: 'emerald' }
              )}
            </div>
          )}

          {activeTab === 'execution' && (
            <div className="grid gap-5 xl:grid-cols-2">
              {renderSectionCard(
                'Execution Controls',
                <div className="space-y-4">
                  {renderMetricGrid([
                    { label: 'Outreach State', value: outreachBlocked ? 'Blocked' : 'Open' },
                    { label: 'Lead Source', value: leadGeneratorSourceLabel || 'Unknown' },
                    { label: 'Meeting URL', value: meetingUrlValue || 'Not configured' },
                  ])}
                  <div>
                    <div className="mb-2 text-[10px] font-black uppercase tracking-[0.16em] text-gray-500">Outreach Channels</div>
                    {renderChipList(outreachChannelLabels, 'cyan')}
                  </div>
                  <div>
                    <div className="mb-2 text-[10px] font-black uppercase tracking-[0.16em] text-gray-500">Available Outreach Assets</div>
                    {renderChipList(availableOutreachAssetsValue, 'slate')}
                  </div>
                </div>,
                { subtitle: 'The practical settings the team needs before sending outreach.', accent: 'slate' }
              )}
              {renderSectionCard(
                'Outreach Timing',
                renderRichTextBlock(campaign.best_outreach_timing || 'No timing guidance configured.'),
                { subtitle: 'When and how outreach should be timed for the best response.', accent: 'amber' }
              )}
              {renderSectionCard(
                'Sales Frameworks',
                renderRichTextBlock(
                  campaign.sales_frameworks && typeof campaign.sales_frameworks === 'object' && 'value' in campaign.sales_frameworks
                    ? (campaign.sales_frameworks as any).value
                    : campaign.sales_frameworks || 'No sales frameworks selected.'
                ),
                { subtitle: 'Frameworks or playbooks attached to this campaign.', accent: 'blue' }
              )}
              {renderSectionCard(
                'Success Metrics',
                renderRichTextBlock(campaign.success_metrics || 'No success metrics configured.'),
                { subtitle: 'How the team should judge whether this campaign is working.', accent: 'emerald' }
              )}
            </div>
          )}

          {activeTab === 'offer' && (
            <div className="grid gap-5 xl:grid-cols-2">
              {renderSectionCard(
                'Offer Positioning',
                <div className="space-y-4">
                  {renderMetricGrid([
                    { label: 'USP', value: uspValue || 'Not provided' },
                    { label: 'Guarantee', value: guaranteeValue || 'Not provided' },
                    { label: 'Offer Pricing', value: offerPricingValue || 'Not provided' },
                  ])}
                  {campaign.core_value_proposition && (
                    <div>
                      <div className="mb-2 text-[10px] font-black uppercase tracking-[0.16em] text-gray-500">Core Value Proposition</div>
                      {renderRichTextBlock(campaign.core_value_proposition)}
                    </div>
                  )}
                </div>,
                { subtitle: 'The commercial story behind the campaign and how the offer is framed.', accent: 'blue' }
              )}
              {renderSectionCard(
                'Market Context',
                <div className="space-y-4">
                  {campaign.product_website && (
                    <div>
                      <div className="mb-2 text-[10px] font-black uppercase tracking-[0.16em] text-gray-500">Product Website</div>
                      <a href={campaign.product_website} target="_blank" rel="noopener noreferrer" className="break-all text-sm font-semibold text-[#C7D2FF] hover:text-white">
                        {campaign.product_website}
                      </a>
                    </div>
                  )}
                  {campaign.competitor_analysis && (
                    <div>
                      <div className="mb-2 text-[10px] font-black uppercase tracking-[0.16em] text-gray-500">Competitor Analysis</div>
                      {renderRichTextBlock(campaign.competitor_analysis)}
                    </div>
                  )}
                  {campaign.industry_keyworks && (
                    <div>
                      <div className="mb-2 text-[10px] font-black uppercase tracking-[0.16em] text-gray-500">Industry Keywords</div>
                      {renderRichTextBlock(campaign.industry_keyworks)}
                    </div>
                  )}
                </div>,
                { subtitle: 'Supporting context around the market, competitors, and product surface area.', accent: 'cyan' }
              )}
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="grid gap-5 xl:grid-cols-2">
              {renderSectionCard(
                'Additional Notes',
                renderRichTextBlock(campaign.other_notes || 'No additional notes available.'),
                { subtitle: 'Extra information, caveats, or instructions for the team.', accent: 'slate' }
              )}
              {campaign.campaign_context_file && renderSectionCard(
                'Context File',
                <div className="rounded-xl bg-black/20 p-4 text-sm text-gray-200">
                  {campaign.campaign_context_file}
                </div>,
                { subtitle: 'Linked context asset or supporting file reference.', accent: 'cyan' }
              )}
            </div>
          )}

          {activeTab === 'admin' && (
            <div className="grid gap-5 xl:grid-cols-2">
              {renderSectionCard(
                'Reference Data',
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-[0.16em] text-gray-500">Campaign ID</div>
                    <div className="mt-2 break-all text-sm font-semibold text-white">{campaign.id}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-[0.16em] text-gray-500">Client Email</div>
                    <div className="mt-2 break-all text-sm font-semibold text-white">{campaign.client_email || 'Not available'}</div>
                  </div>
                </div>,
                { subtitle: 'Identifiers and references kept here so the main tabs stay cleaner.', accent: 'slate' }
              )}
              {renderSectionCard(
                'System Timeline',
                <div className="space-y-4">
                  {renderMetricGrid([
                    { label: 'Created', value: formatDate(campaign.created || campaign.createdAt) },
                    { label: 'Updated', value: formatDate(campaign.updatedAt) },
                    { label: 'Status', value: formatStatusLabel(campaign.status) },
                    { label: 'Client Email', value: campaign.client_email || 'Not available' },
                  ])}
                </div>,
                { subtitle: 'Administrative metadata and supporting references for operators and owners.', accent: 'slate' }
              )}
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Invite Management Modal */}
      <InviteManagementModal
        campaign={showInviteModal ? campaign : null}
        onClose={() => setShowInviteModal(false)}
      />
    </div>,
    document.body
  );
};
